// hooks/useSEOData.ts
import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import useDocumentTitle from './useDocumentTitle';
import usePathSegments from './usePathSegments';
import { CategoryData, PostData } from '../types/types';

const useSEOData = (catData?: CategoryData, postData?: PostData) => {
  const location = useLocation();
  const { pathSegments, topLevelSlug } = usePathSegments();
  const isPostPath = pathSegments.includes('file');

  // Enhanced function to build breadcrumb with actual names from GraphQL data
  const buildCategoryBreadcrumb = (segments: string[], rootCategory?: CategoryData): string[] => {
    if (!rootCategory) return [topLevelSlug];
    
    const breadcrumb = [rootCategory.name]; // Start with root category name (e.g., "Music")
    let currentCategory = rootCategory;
    
    // Walk through segments (skipping the first one which is the root, and stopping at 'file')
    for (let i = 1; i < segments.length; i++) {
      const segment = segments[i];
      if (segment === 'file') break; // Stop at 'file' segment
      
      // Find the child category with this slug
      const nextCategory = currentCategory.children?.nodes.find(child => child.slug === segment);
      if (nextCategory) {
        breadcrumb.push(nextCategory.name); // Use actual name from GraphQL data
        currentCategory = nextCategory;
      } else {
        // Fallback to transformed slug if category not found in data
        breadcrumb.push(toTitleCase(segment.replace(/-/g, ' ')));
      }
    }
    
    return breadcrumb;
  };

  // Helper function for fallback title case transformation
  const toTitleCase = (str: string) => {
    return str.replace(
      /\w\S*/g,
      text => text.charAt(0).toUpperCase() + text.substring(1).toLowerCase()
    );
  };

  const seoData = useMemo(() => {
    const titleParts = [];
    
    // 1. If viewing a post/file, start with the actual post title
    if (isPostPath && postData) {
      titleParts.push(postData.title);
    }
    
    // 2. Build category hierarchy using actual names from GraphQL data
    const categoryBreadcrumb = buildCategoryBreadcrumb(pathSegments, catData);
    
    // Add category breadcrumb to title parts
    if (isPostPath) {
      // For posts: "Post Title | Category | Parent Category | Music"
      titleParts.push(...categoryBreadcrumb.reverse()); // Reverse to go from specific to general
    } else {
      // For category browsing: "Category | Parent Category | Music"
      titleParts.push(...categoryBreadcrumb.slice().reverse()); // Show the hierarchy
    }
    
    // Build final title
    const finalTitle = titleParts.length > 0 ? titleParts.join(' | ') : 'Fatima Al Qadiri';
    
    // Generate description
    const description = generateDescription(titleParts, isPostPath, postData, catData);
    
    return {
      title: finalTitle,
      description,
      canonical: `https://fatimaalqadiri.com${location.pathname}`,
    };
  }, [pathSegments, isPostPath, postData, catData, location.pathname, topLevelSlug]);

  useDocumentTitle(seoData);
  return seoData;
};

const generateDescription = (
  titleParts: string[], 
  isPostPath: boolean, 
  postData?: PostData, 
  catData?: CategoryData
) => {
  if (isPostPath && postData) {
    // For posts, try to use content or create a descriptive text
    if (postData.content) {
      // Strip HTML and truncate
      const plainText = postData.content.replace(/<[^>]*>/g, '');
      return plainText.length > 160 
        ? plainText.substring(0, 157) + '...'
        : plainText;
    }
    return `${postData.title} by Fatima Al Qadiri`;
  }
  
  // For categories
  const categoryContext = titleParts.slice(1).join(' in '); // Skip the first title part
  return categoryContext 
    ? `Explore ${categoryContext} by Fatima Al Qadiri`
    : 'Fatima Al Qadiri is a New York-based artist, musician and composer.';
};

export default useSEOData;